import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import http from '../../utils/http';
import { apiRoutes } from '../../routes/api';

// Async thunks
export const checkGoogleAuthStatus = createAsyncThunk('googleBusiness/checkAuthStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await http.get(apiRoutes.google_status);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || 'Failed to check auth status');
  }
});

export const initiateGoogleAuth = createAsyncThunk('googleBusiness/initiateAuth', async (_, { rejectWithValue }) => {
  try {
    const response = await http.get(apiRoutes.google_auth);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || 'Failed to initiate authorization');
  }
});

export const fetchGoogleReviews = createAsyncThunk('googleBusiness/fetchReviews', async (locationId, { rejectWithValue }) => {
  try {
    const url = locationId ? `${apiRoutes.google_reviews}?locationId=${locationId}` : apiRoutes.google_reviews;
    const response = await http.get(url);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || 'Failed to fetch reviews');
  }
});

const initialState = {
  isAuthorized: false,
  authLoading: false,
  authError: null,
  reviews: [],
  reviewsLoading: false,
  reviewsError: null,
  lastSync: null,
  accountInfo: null,
};

const googleBusinessSlice = createSlice({
  name: 'googleBusiness',
  initialState,
  reducers: {
    setAuthStatus: (state, action) => {
      state.isAuthorized = action.payload.isAuthorized;
      state.lastSync = action.payload.lastSync;
      state.accountInfo = action.payload.accountInfo;
    },
    clearAuthError: (state) => {
      state.authError = null;
    },
    clearReviewsError: (state) => {
      state.reviewsError = null;
    },
    resetGoogleBusiness: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // Check Auth Status
    builder
      .addCase(checkGoogleAuthStatus.pending, (state) => {
        state.authLoading = true;
        state.authError = null;
      })
      .addCase(checkGoogleAuthStatus.fulfilled, (state, action) => {
        state.authLoading = false;
        state.isAuthorized = action.payload?.data.isConnected || false;
        state.lastSync = action.payload?.data.connectedAt || null;
        state.accountInfo = action.payload?.data.accountId || null;
      })
      .addCase(checkGoogleAuthStatus.rejected, (state, action) => {
        state.authLoading = false;
        state.authError = action.payload;
      });

    // Initiate Auth
    builder
      .addCase(initiateGoogleAuth.pending, (state) => {
        state.authLoading = true;
        state.authError = null;
      })
      .addCase(initiateGoogleAuth.fulfilled, (state) => {
        state.authLoading = false;
      })
      .addCase(initiateGoogleAuth.rejected, (state, action) => {
        state.authLoading = false;
        state.authError = action.payload;
      });

    // Fetch Reviews
    builder
      .addCase(fetchGoogleReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchGoogleReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviews = action.payload.reviews || [];
        state.lastSync = action.payload.lastSync || new Date().toISOString();
      })
      .addCase(fetchGoogleReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      });
  },
});

export const { setAuthStatus, clearAuthError, clearReviewsError, resetGoogleBusiness } = googleBusinessSlice.actions;

export default googleBusinessSlice.reducer;
